import { useSelector } from "react-redux";

import { selectIsProfileLoaded, selectRole } from "@/redux/selectors/authSelectors";

import { checkPermissionByRole } from "../helpers/checkPermissionByRole";
import type { CheckPermissionByRoleProps } from "../helpers/checkPermissionByRole";
import type { PermissionContextMap, PermissionKey } from "../types";

export type CheckPermissionProps<K extends PermissionKey> =
  PermissionContextMap[K] extends undefined
    ? { permissionKey: K; predicateContext?: never }
    : { permissionKey: K; predicateContext: PermissionContextMap[K] };

/**
 * Hook to check if the current user has permission to access a feature.
 * Automatically updates when the user's role changes.
 *
 * Pass `predicateContext` when the permission requires it — TypeScript will
 * enforce the correct shape based on the permissionKey.
 *
 * @returns `null` while role is loading, otherwise `boolean`
 */
export function useCheckPermission<K extends PermissionKey>({
  permissionKey,
  predicateContext,
}: CheckPermissionProps<K>): boolean | null {
  const role = useSelector(selectRole);
  const isProfileLoaded = useSelector(selectIsProfileLoaded);

  if (!role) return null;
  // predicateContext defined means the permission needs profile-sourced data — wait for it
  if (predicateContext !== undefined && !isProfileLoaded) return null;

  return checkPermissionByRole({
    permissionKey,
    predicateContext,
    role,
  } as CheckPermissionByRoleProps<K>);
}
